import { FormControl } from "@angular/forms";

export function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
export function toISO(date: Date, minutes: number) {
  const d = new Date(date);
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return d.toISOString();
}

/** Convertit un ISO en minutes depuis minuit du currentDate(); renvoie null si pas le même jour */
export function isoToMinute(date: Date, iso: string): number | null {
  const d = new Date(iso);
  const ref = new Date(this.currentDate);
  if (
    d.getFullYear() !== ref.getFullYear() ||
    d.getMonth() !== ref.getMonth() ||
    d.getDate() !== ref.getDate()
  ) {
    return null; // autre jour -> ignoré pour l'affichage du jour courant
  }
  return d.getHours() * 60 + d.getMinutes();
}

// time-utils.ts
export function dayBounds(date: Date): { dayStart: Date; dayEnd: Date } {
  const d0 = new Date(date);
  d0.setHours(0, 0, 0, 0);
  const d1 = new Date(d0);
  d1.setDate(d1.getDate() + 1);
  return { dayStart: d0, dayEnd: d1 };
}

export function minutesSinceMidnightLocal(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

// src/app/utils/time.utils.ts

/** 0 → "00:00", 75 → "01:15" */
export function minutesToLabel(m: number): string {
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/** Arrondi au pas vers la gauche (floor) */
export function snapLeft(min: number, step: number): number {
  return Math.floor(min / step) * step;
}

/** Arrondi au pas vers la droite (ceil) */
export function snapRight(min: number, step: number): number {
  return Math.ceil(min / step) * step;
}

/** Arrondi au pas en fonction de la direction (-1, 0, +1) */
export function snapInRange(
  min: number,
  dir: -1 | 0 | 1,
  step: number
): number {
  if (dir < 0) return snapLeft(min, step);
  if (dir > 0) return snapRight(min, step);
  return Math.round(min / step) * step;
}

/** Arrondi standard au pas (nearest) */
export function snapMinute(min: number, step: number): number {
  return Math.round(min / step) * step;
}

/** Contraint un nombre de minutes dans l’intervalle [open, close] (en heures) */
export function clampMinute(
  min: number,
  openHour: number,
  closeHour: number
): number {
  const minOpen = openHour * 60;
  const maxClose = closeHour * 60;
  if (min < minOpen) return minOpen;
  if (min > maxClose) return maxClose;
  return min;
}

/** Convertit 1px en minutes selon la largeur d’une heure (px) */
export function onePixelInMinutes(hourWidthPx: number): number {
  return 60 / hourWidthPx;
}

/** Convertit un décalage horizontal (px *depuis l’ouverture*) en minutes absolues */
export function pxToMinutes(
  pxFromOpen: number,
  openHour: number,
  hourWidthPx: number
): number {
  const minutesFromOpen = pxFromOpen * (60 / hourWidthPx);
  return openHour * 60 + minutesFromOpen;
}

/** Convertit des minutes absolues → px *depuis l’ouverture* */
export function minutesToPx(
  min: number,
  openHour: number,
  hourWidthPx: number
): number {
  return (min - openHour * 60) * (hourWidthPx / 60);
}

/** Décalage (px) de l’étiquette d’une heure h */
export function hourLeftPx(
  h: number,
  openHour: number,
  hourWidthPx: number
): number {
  return (h - openHour) * hourWidthPx;
}

/** Tableau des heures entières visibles [open..close] incluses */
export function hoursArray(openHour: number, closeHour: number): number[] {
  return Array.from(
    { length: closeHour - openHour + 1 },
    (_, i) => openHour + i
  );
}

/** Nombre d’heures visibles (close - open + 1) */
export function hoursLength(openHour: number, closeHour: number): number {
  return closeHour - openHour + 1;
}

/** Largeur totale de la timeline en px (ex: 6→22 = 16h) */
export function timelineWidthPx(
  openHour: number,
  closeHour: number,
  hourWidthPx: number
): number {
  return (closeHour - openHour) * hourWidthPx;
}

/**
 * Normalise une plage [selStart, selEnd] dans la journée :
 * - borne dans [open..close]
 * - garantit un minimum de `step`
 * - retourne { startMin, endMin }
 */
export function normalizeRangeWithinDay(
  selStart: number,
  selEnd: number,
  openHour: number,
  closeHour: number,
  step: number
): { startMin: number; endMin: number } {
  const openMin = openHour * 60;
  const closeMin = closeHour * 60;
  const start = Math.max(openMin, Math.min(selStart, closeMin - step));
  const end = Math.max(start + step, Math.min(selEnd, closeMin));
  return { startMin: start, endMin: end };
}

/**
 * Calcule un créneau "rapide" autour de l’heure courante,
 * arrondi au pas, dans l’intervalle [open..close].
 * Par défaut : durée max 60min et min = step.
 */
export function defaultSlot(
  now: Date,
  openHour: number,
  closeHour: number,
  step: number
): { startMin: number; endMin: number } {
  const minutesNow = now.getHours() * 60 + now.getMinutes();
  const openMin = openHour * 60;
  const closeMin = closeHour * 60;
  const snap = (m: number) => Math.round(m / step) * step;

  const start = snap(Math.max(openMin, Math.min(minutesNow, closeMin - step)));
  const end = Math.max(start + step, Math.min(start + 180, closeMin));
  return { startMin: start, endMin: end };
}

// Durées (illimité, signe)
export function minutesToHhMm(totalMinutes: number): string {
  if (totalMinutes == null || !isFinite(totalMinutes)) return "0:00";

  const mins = Math.trunc(totalMinutes); // tronque si nombre à virgule
  const sign = mins < 0 ? "-" : "";
  const abs = Math.abs(mins);

  const hours = Math.floor(abs / 60); // AUCUN padding ici
  const minutes = abs % 60;

  const mm = String(minutes).padStart(2, "0"); // minutes sur 2 digits

  return `${sign}${hours}:${mm}`;
}
// Durées (illimité, signe)
export function hhMmToMinutes(input: string): number {
  const m = String(input)
    .trim()
    .match(/^([+-])?\s*(\d+)\s*:\s*([0-5]?\d)\s*$/);
  if (!m) {
    throw new Error("Format invalide. Attendu: H:MM (ex: 2:05, -12:30)");
  }
  const sign = m[1] === "-" ? -1 : 1;
  const hours = parseInt(m[2], 10);
  const minutes = parseInt(m[3], 10);
  return sign * (hours * 60 + minutes);
}
// Heures du jour (00–23h, wrap 24h)
export function timeStrToMinutesHHmm(
  s: string | null | undefined
): number | null {
  const m = String(s ?? "")
    .trim()
    .match(/^(\d{1,2}):([0-5]\d)$/);
  if (!m) return null;
  const hh = +m[1],
    mm = +m[2];
  if (hh < 0 || hh > 23) return null;
  return hh * 60 + mm;
}
// Heures du jour (00–23h, wrap 24h)
export function minutesToTimeHHmm(mins: number): string {
  // wrap sur 24h (gère +/-, dépassements)
  let m = ((Math.trunc(mins) % 1440) + 1440) % 1440;
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/** Normalise un texte libre en H:mm (insertion auto de ':') */
export function normalizeHhMmText(s: string): string {
  if (!s) return "0:00";
  s = s.trim();

  // 1) Déjà au format H:mm → reformat propre
  const parsed = this.tryHhMmToMinutes(s);
  if (parsed != null) return this.minutesToHhMm(parsed);

  // 2) Sinon: chiffres seuls → déduire heures/minutes
  const sign = s.startsWith("-") ? "-" : "";
  const digits = s.replace(/^[+-]/, "").replace(/\D+/g, ""); // garde que les chiffres
  if (!digits) return "0:00";

  let hours = 0,
    minutes = 0;

  if (digits.length <= 2) {
    // 1–2 chiffres = minutes
    minutes = parseInt(digits, 10);
  } else {
    hours = parseInt(digits.slice(0, -2), 10);
    minutes = parseInt(digits.slice(-2), 10);
  }

  // report si minutes >= 60
  hours += Math.floor(minutes / 60);
  minutes = minutes % 60;

  return `${sign}${hours}:${String(minutes).padStart(2, "0")}`;
}

export function diffMinutes(a: string | Date, b: string | Date): number {
  const t1 = toMillis(a);
  const t2 = toMillis(b);
  return Math.round((t1 - t2) / 60000);
}

function toMillis(value: string | Date): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "string") {
    const d = new Date(value); // "2025-10-02T07:55:00.000Z" => OK
    const t = d.getTime();
    if (!Number.isNaN(t)) return t;
  }
  throw new TypeError(`Date invalide pour diffMinutes: ${String(value)}`);
}
